import { Button, Select, Space, DatePicker, Input } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

export default function FilterBuilder({ filters, onChange, availableFields = [] }) {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = useState(filters || []);

  const OPERATORS = [
    { value: "equals", label: t("common.operators.equals") },
    { value: "notEquals", label: t("common.operators.notEquals") },
    { value: "contains", label: t("common.operators.contains") },
    { value: "notContains", label: t("common.operators.notContains") },
    { value: "startsWith", label: t("common.operators.startsWith") },
    { value: "endsWith", label: t("common.operators.endsWith") },
    { value: "in", label: t("common.operators.in") },
    { value: "notIn", label: t("common.operators.notIn") },
    { value: "greaterThan", label: t("common.operators.greaterThan") },
    { value: "lessThan", label: t("common.operators.lessThan") },
    { value: "greaterThanOrEqual", label: t("common.operators.greaterThanOrEqual") },
    { value: "lessThanOrEqual", label: t("common.operators.lessThanOrEqual") },
    { value: "isNull", label: t("common.operators.isNull") },
    { value: "isNotNull", label: t("common.operators.isNotNull") },
  ];

  const LOGIC_OPERATORS = [
    { value: "and", label: t("common.operators.and") },
    { value: "or", label: t("common.operators.or") },
  ];

  const handleAddFilter = () => {
    const newFilter = {
      field: "",
      operator: "equals",
      value: "",
      logic: "and",
    };
    setLocalFilters([...localFilters, newFilter]);
    onChange([...localFilters, newFilter]);
  };

  const handleRemoveFilter = (index) => {
    const newFilters = localFilters.filter((_, i) => i !== index);
    setLocalFilters(newFilters);
    onChange(newFilters);
  };

  const handleFilterChange = (index, field, value) => {
    const newFilters = [...localFilters];
    newFilters[index] = {
      ...newFilters[index],
      [field]: value,
    };
    
    // إعادة تعيين القيمة عند تغيير الحقل أو المشغل
    if (field === "field" || field === "operator") {
      newFilters[index].value = "";
    }
    
    setLocalFilters(newFilters);
    onChange(newFilters);
  };

  const getFieldType = (fieldKey) => {
    const field = availableFields.find((f) => f.key === fieldKey);
    return field?.type || "text";
  };

  const renderValueInput = (filter, index) => {
    const fieldType = getFieldType(filter.field);
    const operator = filter.operator;

    if (operator === "isNull" || operator === "isNotNull") {
      return null;
    }

    if (operator === "dateRange") {
      return (
        <RangePicker
          value={
            filter.value && Array.isArray(filter.value)
              ? [dayjs(filter.value[0]), dayjs(filter.value[1])]
              : null
          }
          onChange={(dates) => {
            if (dates && dates.length === 2) {
              handleFilterChange(index, "value", [
                dates[0].format("YYYY-MM-DD"),
                dates[1].format("YYYY-MM-DD"),
              ]);
            } else {
              handleFilterChange(index, "value", null);
            }
          }}
          format="YYYY-MM-DD"
        />
      );
    }

    if (operator === "between") {
      return (
        <Space>
          <Input
            placeholder="من"
            value={filter.value?.[0] || ""}
            onChange={(e) => {
              const newValue = [e.target.value, filter.value?.[1] || ""];
              handleFilterChange(index, "value", newValue);
            }}
          />
          <Input
            placeholder="إلى"
            value={filter.value?.[1] || ""}
            onChange={(e) => {
              const newValue = [filter.value?.[0] || "", e.target.value];
              handleFilterChange(index, "value", newValue);
            }}
          />
        </Space>
      );
    }

    if (operator === "in" || operator === "notIn") {
      const field = availableFields.find((f) => f.key === filter.field);
      if (field && field.options) {
        return (
          <Select
            mode="multiple"
            placeholder="اختر القيم"
            value={Array.isArray(filter.value) ? filter.value : []}
            onChange={(value) => handleFilterChange(index, "value", value)}
            options={field.options}
            className="min-w-[200px]"
          />
        );
      }
      return (
        <Input
          placeholder="أدخل القيم مفصولة بفواصل"
          value={filter.value || ""}
          onChange={(e) => handleFilterChange(index, "value", e.target.value)}
        />
      );
    }

    if (fieldType === "select" && operator === "equals") {
      const field = availableFields.find((f) => f.key === filter.field);
      if (field && field.options) {
        return (
          <Select
            placeholder="اختر قيمة"
            value={filter.value}
            onChange={(value) => handleFilterChange(index, "value", value)}
            options={field.options}
            className="min-w-[200px]"
          />
        );
      }
    }

    if (fieldType === "dateRange" && ["equals", "notEquals", "greaterThan", "lessThan", "greaterThanOrEqual", "lessThanOrEqual"].includes(operator)) {
      return (
        <DatePicker
          className="min-w-[200px]"
          value={filter.value ? dayjs(filter.value) : null}
          onChange={(date) => {
            handleFilterChange(index, "value", date ? date.format("YYYY-MM-DD") : null);
          }}
          format="YYYY-MM-DD"
        />
      );
    }

    return (
      <Input
        placeholder="أدخل القيمة"
        value={filter.value || ""}
        onChange={(e) => handleFilterChange(index, "value", e.target.value)}
      />
    );
  };

  return (
    <div className="space-y-3">
      {localFilters.map((filter, index) => (
        <div key={index} className="p-3 border rounded-lg bg-gray-50">
          <Space direction="vertical" className="w-full" size="small">
            <Space wrap>
              {index > 0 && (
                <Select
                  value={filter.logic}
                  onChange={(value) => handleFilterChange(index, "logic", value)}
                  options={LOGIC_OPERATORS}
                  className="w-20"
                />
              )}
              <Select
                placeholder="اختر الحقل"
                value={filter.field}
                onChange={(value) => handleFilterChange(index, "field", value)}
                options={availableFields.map((f) => ({
                  value: f.key,
                  label: f.label,
                }))}
                className="min-w-[150px]"
              />
              <Select
                value={filter.operator}
                onChange={(value) => handleFilterChange(index, "operator", value)}
                options={OPERATORS}
                className="min-w-[150px]"
              />
              {renderValueInput(filter, index)}
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleRemoveFilter(index)}
              />
            </Space>
          </Space>
        </div>
      ))}
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={handleAddFilter}
        block>
        {t("common.addFilter")}
      </Button>
    </div>
  );
}
